/**
 * AuthContext — Gestion de l'authentification
 *
 * Fournit :
 * - login/logout
 * - Utilisateur courant (depuis localStorage + vérification API)
 * - État de chargement
 * - Gestion du flux 2FA (TOTP)
 */

import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  type ReactNode,
} from 'react';
import { apiFetch, setToken, removeToken } from '../utils/api.js';
import type { AuthUser, LoginResult } from '../types/auth.js';

interface AuthContextValue {
  user: AuthUser | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (email: string, password: string) => Promise<LoginResult>;
  verifyTotp: (tempToken: string, totpCode: string) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

/** Charge l'utilisateur depuis le localStorage */
function loadStoredUser(): AuthUser | null {
  try {
    const stored = localStorage.getItem('user');
    if (stored) return JSON.parse(stored) as AuthUser;
  } catch {
    localStorage.removeItem('user');
  }
  return null;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(loadStoredUser);
  const [isLoading, setIsLoading] = useState(true);

  // Au montage, vérifier que le token est encore valide
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      setUser(null);
      setIsLoading(false);
      return;
    }

    apiFetch<{ user: AuthUser }>('/auth/me')
      .then((res) => {
        if (res.success && res.data) {
          setUser(res.data.user);
          localStorage.setItem('user', JSON.stringify(res.data.user));
        } else {
          setUser(null);
          removeToken();
          localStorage.removeItem('user');
        }
      })
      .catch(() => {
        // Garder l'utilisateur en cache si hors ligne
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, []);

  const login = useCallback(async (email: string, password: string): Promise<LoginResult> => {
    const res = await apiFetch<LoginResult>('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });

    if (!res.success || !res.data) {
      throw new Error(res.error ?? 'Erreur de connexion');
    }

    // Si 2FA requise, retourner le résultat tel quel
    if ('requiresTOTP' in res.data) {
      return res.data;
    }

    // Connexion directe
    setToken(res.data.token);
    setUser(res.data.user);
    localStorage.setItem('user', JSON.stringify(res.data.user));
    return res.data;
  }, []);

  const verifyTotp = useCallback(async (tempToken: string, totpCode: string): Promise<void> => {
    const res = await apiFetch<{ token: string; user: AuthUser }>('/auth/verify-totp', {
      method: 'POST',
      body: JSON.stringify({ tempToken, totpCode }),
    });

    if (!res.success || !res.data) {
      throw new Error(res.error ?? 'Code TOTP invalide');
    }

    setToken(res.data.token);
    setUser(res.data.user);
    localStorage.setItem('user', JSON.stringify(res.data.user));
  }, []);

  const logout = useCallback(async (): Promise<void> => {
    try {
      await apiFetch('/auth/logout', { method: 'POST' });
    } catch {
      // Déconnecter localement même si l'API échoue
    }
    removeToken();
    localStorage.removeItem('user');
    setUser(null);
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        isLoading,
        isAuthenticated: !!user,
        login,
        verifyTotp,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

/** Hook pour accéder au contexte d'authentification */
export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth doit être utilisé dans un AuthProvider');
  }
  return ctx;
}
